import * as React from 'react'
import { useIsOffline, useAuth } from '@oneblink/apps-react'
import { submissionService } from '@oneblink/apps'
import styled from 'styled-components'
import { v4 } from 'uuid'
import { FormTypes } from '@oneblink/types'
import { motion, AnimatePresence } from 'framer-motion'
import _cloneDeep from 'lodash.clonedeep'

import ControlledForm from '../ControlledForm'
import FormFooter, { IconContainer } from '../../../components/Layout/Footer'
import BlankModal from '../../../components/BlankModal'
import ErrorMessage from 'components/ErrorMessage'
import { Map } from '@styled-icons/material'

import { findElement, updateOptions } from 'lib/definition-manipulations'
import { useReferenceData } from 'Providers/ReferenceDataProvider'
import useUpdateSubmission from './useUpdateSubmission'
import updateDefinition from 'lib/definition/update-definition'
import { ChoiceElementOption } from '@oneblink/types/typescript/forms'

const MapIcon = styled(Map)`
  height: 2.5rem;
  align-self: center;
  padding: 0 1rem;
`
MapIcon.displayName = 'MapIcon'

const CattleTickContainer = styled.div<{ $isOffline: boolean }>`
  padding-bottom: ${({ $isOffline }) => ($isOffline ? '0px' : '64px')};
`
CattleTickContainer.displayName = 'CattleTickContainer'

function alphabeticallySortLabel(
  a: FormTypes.ChoiceElementOption,
  b: FormTypes.ChoiceElementOption,
) {
  const lowerA = a.label.toLowerCase()
  const lowerB = b.label.toLowerCase()
  if (lowerA < lowerB) {
    return -1
  }
  if (lowerA > lowerB) {
    return 1
  }
  return 0
}

export default function CattleTick({
  formDefinition,
  preFillData,
  existingDraft,
  buttons,
}: {
  formDefinition: FormTypes.Form
  preFillData: Record<string, unknown>
  autoSaveKey?: string
  existingDraft?: DraftAndData
  buttons?: Record<string, unknown>
}) {
  const { referenceData } = useReferenceData()
  const { userProfile } = useAuth()
  const isOffline = useIsOffline()
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [isFooterDisplayed, setIsFooterDisplayed] = React.useState(false)
  const updateSubmissionCallback = useUpdateSubmission()
  const updateSubmission = React.useCallback(
    (submission: Record<string, unknown>, definition: FormTypes.Form) => {
      // cant set state in a render function anymore :(
      setTimeout(
        () => setIsFooterDisplayed(() => submission.Entering_NSW === 'Yes'),
        0,
      )
      return updateSubmissionCallback(submission, definition)
    },
    [updateSubmissionCallback],
  )

  const customSubmit = React.useCallback(
    (newFormSubmission: submissionService.NewFormSubmission) => {
      //modify newFormSubmission here
      return newFormSubmission
    },
    [],
  )

  const nominatedAuthorisedOfficers = React.useMemo(
    () => referenceData?.find((d) => d.type === 'nominatedAOs'),
    [referenceData],
  )

  const certificateIssuingAuthorisedOfficers = React.useMemo(
    () => referenceData?.find((d) => d.type === 'certificateIssuingAOs'),
    [referenceData],
  )

  const noPicReasonsData = React.useMemo(
    () => referenceData?.find((d) => d.type === 'noPICReasons'),
    [referenceData],
  )

  const isOfficer = React.useMemo(
    () => /@dpi(e)?\.nsw\.gov\.au$/.test(userProfile?.email || ''),
    [userProfile?.email],
  )

  //only need to do this once
  const modifiedDefinition = React.useMemo(() => {
    let bioCertNameUpdated = false
    let noPicReasonsUpdated = false
    let emailCCUpdated = false
    if (
      certificateIssuingAuthorisedOfficers?.type === 'certificateIssuingAOs'
    ) {
      const bioCertOptions = certificateIssuingAuthorisedOfficers?.value
        .map((ciao) => ({
          id: v4(),
          label: [ciao.First_Name, ciao.Last_Name].join(' '),
          value: ciao.Employee_Id,
        }))
        .sort(alphabeticallySortLabel)

      bioCertNameUpdated = updateOptions({
        definition: formDefinition,
        elementName: 'Biosecurity_Cert_Authorised_officers_name',
        options: bioCertOptions,
      })
    }

    if (nominatedAuthorisedOfficers?.type === 'nominatedAOs') {
      const emailSet = new Set<string>()
      const emailCCOptions = nominatedAuthorisedOfficers.value
        .reduce((opts: FormTypes.ChoiceElementOption[], nao) => {
          if (!nao.Work_Email || emailSet.has(nao.Work_Email)) {
            return opts
          }
          emailSet.add(nao.Work_Email)
          opts.push({
            id: v4(),
            label: [nao.First_Name, nao.Last_Name].join(' '),
            value: nao.Work_Email,
          })
          return opts
        }, [])
        .sort(alphabeticallySortLabel)

      emailCCUpdated = updateOptions({
        definition: formDefinition,
        elementName: 'Email_CC',
        options: emailCCOptions,
      })
    }

    if (noPicReasonsData?.type === 'noPICReasons') {
      const noPicReasonOptions: ChoiceElementOption[] = noPicReasonsData.value
        .map((noPicReason) => ({
          id: v4(),
          label: noPicReason.No_PIC_Reason,
          value: noPicReason.CTROM_No_PIC_Reason_Id,
        }))
        .sort(alphabeticallySortLabel)

      const originUpdated = updateOptions({
        definition: formDefinition,
        elementName: 'reason_No_Origin_Pic',
        options: noPicReasonOptions,
      })

      const destinationUpdated = updateOptions({
        definition: formDefinition,
        elementName: 'reason_No_Destination_Pic',
        options: noPicReasonOptions,
      })

      noPicReasonsUpdated = originUpdated && destinationUpdated
    }

    if (isOfficer) {
      const originStockOwnerElement = findElement(
        formDefinition,
        'Origin_Stock_Owner',
      )

      if (
        originStockOwnerElement?.value &&
        originStockOwnerElement.value.type === 'radio'
      ) {
        originStockOwnerElement.value.readOnly = true
      }

      const movementDateElement = findElement(formDefinition, 'Movement_Date')

      if (
        movementDateElement?.value &&
        movementDateElement.value.type === 'date'
      ) {
        movementDateElement.value.fromDate = undefined
        movementDateElement.value.fromDateDaysOffset = undefined
        movementDateElement.value.fromDateElementId = undefined
        movementDateElement.value.toDate = undefined
        movementDateElement.value.toDateDaysOffset = undefined
        movementDateElement.value.toDateElementId = undefined
      }
    }

    if (!bioCertNameUpdated || !noPicReasonsUpdated || !emailCCUpdated) {
      return
    }

    return _cloneDeep(formDefinition)
  }, [
    certificateIssuingAuthorisedOfficers,
    nominatedAuthorisedOfficers,
    noPicReasonsData,
    isOfficer,
    formDefinition,
  ])

  const updateDefinitionCallback = React.useCallback(
    (currentDefinition: FormTypes.Form, submission: Record<string, unknown>) =>
      updateDefinition({
        currentDefinition,
        originalDefinition: formDefinition,
        submission,
        referenceData: referenceData || [],
        userProfile,
        isOffline,
      }),
    [formDefinition, isOffline, referenceData, userProfile],
  )

  // update prefill based on nominatedAuthorisedOfficers
  if (!existingDraft) {
    if (isOfficer) {
      preFillData.isDPIOfficer__exclude__hidden = true
      preFillData.DPI_Officers_Name = `${userProfile?.firstName} ${userProfile?.lastName}`
    }
  }

  if (!modifiedDefinition || !referenceData) {
    //reference data provider should take care of this already
    return (
      <ErrorMessage>
        An error has occurred while attempting to load the {formDefinition.name}{' '}
        form
      </ErrorMessage>
    )
  }

  return (
    <CattleTickContainer $isOffline={isOffline}>
      <ControlledForm
        form={modifiedDefinition}
        preFillData={{
          firstName: userProfile?.firstName,
          lastName: userProfile?.lastName,
          email: userProfile?.email,
          User_Phone_number: userProfile?.phoneNumber,
          ...preFillData,
        }}
        existingDraft={existingDraft}
        autoSaveKey="CTROM_"
        buttons={buttons}
        updateDefinition={updateDefinitionCallback}
        updateSubmission={updateSubmission}
        customSubmit={customSubmit}
      />
      <BlankModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        desktopContentWidth="80%"
      >
        {isModalOpen && (
          <iframe
            scrolling="no"
            title="QLD Cattle Tick Areas"
            src="//www.arcgis.com/home/webmap/viewer.html?webmap=dc39d0aad4a64c30acc438f8c5dcd4e9&extent=133.3416,-29.6449,161.7303,-10.9572"
            width="100%"
            height="600"
          ></iframe>
        )}
      </BlankModal>
      <AnimatePresence>
        {!isOffline && isFooterDisplayed && (
          <motion.div
            style={{
              height: 72,
              position: 'fixed',
              left: 0,
              width: '100%',
              bottom: 0,
            }}
            initial={{ y: 72 }}
            animate={{ y: 0 }}
            exit={{ y: 72 }}
            transition={{ ease: 'easeOut', duration: 0.25 }}
          >
            <FormFooter>
              <IconContainer onClick={() => setIsModalOpen(true)}>
                <MapIcon />
                View map
              </IconContainer>
            </FormFooter>
          </motion.div>
        )}
      </AnimatePresence>
    </CattleTickContainer>
  )
}
